import siteStats from "@/public/stats.json";

// Single source for every site statistic.
//
// public/stats.json is written once at dev/build time by
// scripts/fetch-stats.mjs and imported statically — same pattern as
// public/states.json / public/countries.json. Baked into the bundle at build
// time; never fetched from the browser.
//
// Upstream: the Cloudflare Worker at the repo root (wrangler.toml +
// worker.js) runs a daily cron that calls the backend's encrypted
// POST /stats/site, decrypts it, stores it in Workers KV, then triggers a
// Pages rebuild — which re-runs fetch-stats.mjs and refreshes this file.
// Nothing in this Next.js app calls that API or holds the decryption key.
//
// This replaced four endpoints / four KV keys / four JSON files with one of
// each, which is why every section below reads from the same object.

export type CategoryRow = { name: string; projects: number; customers: number };

type SiteStats = {
  homepage: { projects_delivered: number; happy_customers: number; global_partners: number };
  vilvad2c: { total_orders: number; total_order_value: number };
  whatsease: {
    messages_delivered: number;
    messages_read: number;
    contacts_managed: number;
    campaigns_run: number;
  };
  verification: {
    verifications: number;
    /** Already a percentage, e.g. 99.4 — not a 0-1 fraction. */
    success_rate: number;
    apis_live: number;
    clients: number;
  };
  marketing: {
    leads_captured: number;
    channels_tracked: number;
    /**
     * Percentage, e.g. 18.6 — or null when the backend could not work out which
     * lead statuses count as won. Null means "don't show this", not "zero".
     */
    conversion_rate: number | null;
  };
  categories: CategoryRow[];
  updated_at: string | null;
};

// `as unknown as` because the on-disk public/stats.json snapshot can briefly
// have the pre-rebrand shape (old key name) until the Worker's cron re-fetches
// from the backend — a real, expected transient state, not a type error. Every
// export below already defends against a missing/renamed section at runtime.
const raw = siteStats as unknown as SiteStats;

// Defensive reads: a section can legitimately be absent if its database was
// unreachable when the snapshot was taken, and a missing key must not crash
// the whole build.
export const HOMEPAGE = raw.homepage ?? { projects_delivered: 0, happy_customers: 0, global_partners: 0 };
export const VILVAD2C = raw.vilvad2c ?? { total_orders: 0, total_order_value: 0 };
export const WHATSEASE = raw.whatsease ?? {
  messages_delivered: 0,
  messages_read: 0,
  contacts_managed: 0,
  campaigns_run: 0,
};
export const VERIFICATION = raw.verification ?? {
  verifications: 0,
  success_rate: 0,
  apis_live: 0,
  clients: 0,
};
export const MARKETING = raw.marketing ?? {
  leads_captured: 0,
  channels_tracked: 0,
  conversion_rate: null,
};
export const CATEGORIES: CategoryRow[] = raw.categories ?? [];

/** When the snapshot was taken. One timestamp now covers every stat block. */
export const STATS_UPDATED_AT: string | null = raw.updated_at ?? null;
